import { AppErrorType, ApplicationError, CRFError, CRFErrorType, CRFToHTMLService, CoreConfiguration, CoreError, CoreErrorType, MagicNumbers, NamedBindings, Version } from "@dandelion/core";
import { inject, injectable } from 'inversify';
import _ from "lodash";

// TODO: test it, but this probably can be removed from core 0.9.12
@injectable()
export class OVERRIDE_CrfToHTMLService extends CRFToHTMLService {

    /*
    protected worker = new Worker(new URL('./CrfToHtmlWorker.js?worker', import.meta.url), { type: 'module' });
    protected workerPromise: Promise<string> | null = null;
    */

    constructor(
        @inject(NamedBindings.CoreConfiguration) protected coreConfiguration: CoreConfiguration,
    ) {
        // @ts-expect-error
        super(coreConfiguration, new Map<string, any>(), new Map<string, XSLT2Processor>);
    }

    public async getHTMLfromXML(
        xml: string,
        crfName: string,
        crfVersion: Version,
        path: string,
        showGroupsInTree = true,
        showDataclassInstancesInTree = true,
        showSetsInTree = true,
        maxDepth = MagicNumbers.maxDepthForCRFComponentAndTree,
        startingDataClass = '',
        crfRoot = 'model',
        prefixForID = '',
        isDeveloperErrorsComponent = false
    ): Promise<string> {
        try {
            const keyFromParams = OVERRIDE_CrfToHTMLService.getKeyFromParams(...arguments);
            const cachedValue = this.cachedCRFS.get(keyFromParams);

            if (cachedValue) {
                return cachedValue;
            }

            // @ts-expect-error
            let processor: XSLT2Processor;
            const cachedProcessor = this.processorsCache.get(path);

            if (!cachedProcessor) {
                //necessary
                //@ts-expect-error
                processor = new XSLT2Processor();

                try {
                    await processor.importStylesheetURI(path);
                } catch (e) {
                    throw new CoreError(
                        `processor.importStylesheetURI failed for XSLT: ${path}`,
                        CoreErrorType.XSLT_NOT_FOUND,
                        e
                    );
                }

                this.processorsCache.set(path, processor);
            } else {
                processor = cachedProcessor;
            }

            processor.setParameter(null, 'root', crfRoot);
            processor.setParameter(null, 'crfName', crfName);
            processor.setParameter(null, 'crfVersion', crfVersion);
            if (showGroupsInTree) {
                processor.setParameter(null, 'showGroupsInTree', 'true');
            }
            if (showDataclassInstancesInTree) {
                processor.setParameter(null, 'showDataclassInstancesInTree', 'true');
            }
            if (showSetsInTree) {
                processor.setParameter(null, 'showSetsInTree', 'true');
            }
            processor.setParameter(null, 'maxDepth', maxDepth.toString());
            processor.setParameter(null, 'startingDataClass', startingDataClass);
            processor.setParameter(null, 'statisticianMode', this.coreConfiguration.statisticianMode ? 'true' : 'false');
            processor.setParameter(null, 'prefixForID', prefixForID);

            const parser = new DOMParser();
            let xmlDoc;
            let objectToTransform;
            let fragment;

            try {
                xmlDoc = parser.parseFromString(xml, 'text/xml');
            } catch (e) {
                throw new CRFError(
                    `parser.parseFromString failed for CRF: ${crfName} v${crfVersion}`,
                    CRFErrorType.XML_PARSING,
                    e
                );
            }

            if (!isDeveloperErrorsComponent) {
                objectToTransform =
                    startingDataClass === ''
                        ? xmlDoc.getElementsByTagName('dataClass')
                        : xmlDoc.querySelectorAll(`dataClass[name='${startingDataClass}']`);

                if (objectToTransform.length < 1 || !objectToTransform[0]) {
                    throw new CRFError(
                        `Starting Dataclass ${startingDataClass || '[0]'} absent in CRF ${crfName} v${crfVersion}?`,
                        CRFErrorType.STARTING_DATACLASS_NOT_FOUND
                    );
                }

                try {
                    fragment = processor.transformToFragment(objectToTransform[0], document);
                } catch (e) {
                    throw new CoreError(
                        `processor.transformToFragment failed for CRF: ${crfName} v${crfVersion} using schema ${path}`,
                        CoreErrorType.XSL_TRANSFORMATION,
                        e
                    );
                }
            } else {
                const xmlDoc = parser.parseFromString(xml, 'text/xml');
                fragment = processor.transformToFragment(xmlDoc.firstElementChild!, document);
            }

            let resultHTML = '';

            if (!fragment.firstChild) {
                return resultHTML;
            }

            for (const i in fragment.childNodes) {
                // necessary
                // eslint-disable-next-line no-prototype-builtins
                if (fragment.childNodes.hasOwnProperty(i)) {
                    resultHTML += (fragment.childNodes[i] as HTMLElement).outerHTML
                        .replace(/&amp;/g, '&')
                        .replace(/my-custom-template/g, 'template');
                }
            }

            /*
            const args = new Array(...arguments);
            const a = new Promise((resolve, reject) => {
                this.worker.addEventListener('message', (message) => {
                    resolve(message.data);
                }, true);

                this.worker.addEventListener('error', (message) => {
                    reject(message.error);
                }, true);

                this.worker.postMessage(_.cloneDeep(args));
            });

            const resultHTML = await a as string;
            */

            this.cachedCRFS.set(keyFromParams, resultHTML);
            return resultHTML;
        } catch (e) {
            throw new ApplicationError('Generic error in getHTMLfromXML', AppErrorType.GENERIC_ERROR, e);
        }
    }

    protected static getKeyFromParams(...args: Array<any>): string {
        args.splice(0, 1);
        if (args.length < 11 || args[10] !== 'tree-') {
            // We remove arguments that change things only for tree
            args.splice(3, 4);
        }
        return args.reduce((result, arg) => result + arg);
    }
}